import { useEffect, useRef, useState } from 'react';

const useTurnstile = ({ siteKey, theme = 'auto', size = 'normal' } = {}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!siteKey) {
      setIsLoading(false);
      return;
    }

    const scriptId = 'turnstile-script';
    let script = document.getElementById(scriptId);

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const initializeTurnstile = () => {
      if (!window.turnstile || !containerRef.current) return;

      try {
        if (widgetIdRef.current !== null) {
          window.turnstile.remove(widgetIdRef.current);
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size,
          callback: (captchaToken) => {
            setToken(captchaToken);
            setError(null);
            setIsLoading(false);
          },
          'error-callback': () => {
            setError('Turnstile verification failed');
            setToken(null);
            setIsLoading(false);
          },
          'expired-callback': () => {
            setToken(null);
            setError('Verification expired, please try again');
          },
        });

        setIsLoading(false);
      } catch (err) {
        setError(`Failed to initialize Turnstile: ${err.message}`);
        setIsLoading(false);
      }
    };

    if (script.complete || window.turnstile) {
      initializeTurnstile();
    } else {
      script.addEventListener('load', initializeTurnstile);
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.warn('Failed to remove Turnstile widget:', err);
        }
      }
    };
  }, [siteKey, theme, size]);

  const reset = () => {
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
        setToken(null);
        setError(null);
      } catch (err) {
        console.warn('Failed to reset Turnstile:', err);
      }
    }
  };

  return {
    containerRef,
    token,
    isLoading,
    error,
    reset,
  };
};

export default useTurnstile;
